import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Typography,
    IconButton,
    Chip,
    Switch,
    FormControlLabel,
    Grid,
    Paper,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    DeleteOutline as DeleteOutlineIcon,
    DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { useSnackbar } from '../SnackBar';

export default function ChecklistView({ 
    checklists,
    serviceCategories = [],
    onAddOrUpdate, 
    onDeleteChecklist, 
    onToggleStatus,
    onRefresh 
}) {
    const { showSnackbar } = useSnackbar();
    const [openDialog, setOpenDialog] = useState(false);
    const [openItemsDialog, setOpenItemsDialog] = useState(false);
    const [editingChecklist, setEditingChecklist] = useState(null);
    const [viewingChecklist, setViewingChecklist] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        serviceCategoryId: '',
        isActive: true
    });
    const [checklistItems, setChecklistItems] = useState([]);
    const [itemName, setItemName] = useState('');
    const [itemOrder, setItemOrder] = useState(1);
    const [itemRequired, setItemRequired] = useState(false);

    const columns = [
        {
            field: 'name',
            headerName: 'Checklist Name',
            width: 250,
            flex: 1
        },
        {
            field: 'description',
            headerName: 'Description',
            width: 300,
            flex: 1
        },
        {
            field: 'serviceCategoryName',
            headerName: 'Service category',
            width: 180,
            valueGetter: (value, row) => row.serviceCategoryName || '—'
        },
        {
            field: 'checklistItems',
            headerName: 'Items Count',
            width: 120,
            renderCell: (params) => (
                <Chip 
                    label={params.row.checklistItems?.length || 0} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                />
            )
        },
        {
            field: 'isActive',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => (
                <Chip
                    label={params.row.isActive ? 'Active' : 'Inactive'}
                    color={params.row.isActive ? 'success' : 'default'}
                    size="small"
                />
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 250,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Box>
                    <IconButton
                        color="info"
                        size="small"
                        onClick={() => handleViewClick(params.row)}
                        title="View Items"
                    >
                        <VisibilityIcon />
                    </IconButton>
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(params.row)}
                        title="Edit"
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        color="secondary"
                        size="small"
                        onClick={() => handleDeleteClick(params.row.id)}
                        title="Delete"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            )
        }
    ];

    const handleNewClick = () => {
        setEditingChecklist(null);
        setFormData({
            name: '',
            description: '',
            serviceCategoryId: '',
            isActive: true
        });
        setChecklistItems([]);
        setOpenDialog(true);
    };

    const handleEditClick = (checklist) => {
        setEditingChecklist(checklist);
        setFormData({
            name: checklist.name || '',
            description: checklist.description || '',
            serviceCategoryId:
                checklist.serviceCategoryId != null && checklist.serviceCategoryId !== ''
                    ? String(checklist.serviceCategoryId)
                    : '',
            isActive: checklist.isActive !== false
        });
        setChecklistItems(checklist.checklistItems || []);
        setOpenDialog(true);
    };

    const handleViewClick = (checklist) => {
        setViewingChecklist(checklist);
        setOpenItemsDialog(true);
    };

    const handleDeleteClick = async (id) => {
        if (window.confirm('Are you sure you want to delete this checklist?')) {
            try {
                await onDeleteChecklist(id);
                showSnackbar('Checklist deleted successfully!', 'success');
                onRefresh();
            } catch (error) {
                showSnackbar('Failed to delete checklist.', 'error');
            }
        }
    };

    const handleSaveClick = async () => {
        const name = formData.name.trim();
        if (!name) {
            showSnackbar('Checklist name is required', 'error');
            return;
        }
        const categoryId = parseInt(formData.serviceCategoryId, 10);
        if (!formData.serviceCategoryId || Number.isNaN(categoryId) || categoryId <= 0) {
            showSnackbar('Service category is required', 'error');
            return;
        }

        const checklistData = {
            name: name,
            description: formData.description.trim() || '',
            serviceCategoryId: categoryId,
            checklistItems: checklistItems.map((item, index) => ({
                itemText: item.itemText,
                sequenceOrder: item.sequenceOrder || index + 1,
                isRequired: item.isRequired || false
            }))
        };

        try {
            await onAddOrUpdate({ id: editingChecklist?.id, data: checklistData });
            showSnackbar(
                editingChecklist ? 'Checklist updated successfully!' : 'Checklist created successfully!',
                'success'
            );
            setOpenDialog(false);
            onRefresh();
        } catch (error) {
            showSnackbar('Failed to save checklist.', 'error');
        }
    };

    const handleAddItem = () => {
        if (!itemName.trim()) {
            showSnackbar('Item name is required', 'error');
            return;
        }

        const newItem = {
            id: 0,
            itemText: itemName.trim(),
            sequenceOrder: itemOrder,
            isRequired: itemRequired,
            isActive: true
        };

        setChecklistItems([...checklistItems, newItem]);
        setItemName('');
        setItemOrder(checklistItems.length + 2);
        setItemRequired(false);
    };

    const handleRemoveItem = (index) => {
        const newItems = checklistItems.filter((_, i) => i !== index);
        setChecklistItems(newItems);
    };

    const handleMoveItem = (index, direction) => {
        if (
            (direction === 'up' && index === 0) ||
            (direction === 'down' && index === checklistItems.length - 1)
        ) {
            return;
        }

        const newItems = [...checklistItems];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
        
        // Update sequence orders
        newItems.forEach((item, i) => {
            item.sequenceOrder = i + 1;
        });
        
        setChecklistItems(newItems);
    };

    return (
        <main id="main" className="main">
            <div className="pagetitle">
                <h1>Checklists</h1>
                <nav>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item"><a href="/dashboard">Home</a></li>
                        <li className="breadcrumb-item active">Checklists</li>
                    </ol>
                </nav>
            </div>
            <section className="section">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-body">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h5 className="card-title">Manage Checklists</h5>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={handleNewClick}
                                    >
                                        New Checklist
                                    </Button>
                                </div>

                                <div style={{ height: 500, width: '100%' }}>
                                    <DataGrid
                                        rows={checklists}
                                        columns={columns}
                                        pageSizeOptions={[5, 10, 25, 50]}
                                        initialState={{
                                            pagination: {
                                                paginationModel: { page: 0, pageSize: 10 }
                                            }
                                        }}
                                        getRowId={(row) => row.id}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingChecklist ? 'Edit Checklist' : 'Create New Checklist'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Checklist Name *"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    multiline
                                    rows={3}
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel id="checklist-service-category-label">Service category</InputLabel>
                                    <Select
                                        labelId="checklist-service-category-label"
                                        label="Service category"
                                        value={formData.serviceCategoryId}
                                        onChange={(e) =>
                                            setFormData({ ...formData, serviceCategoryId: e.target.value })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Select category</em>
                                        </MenuItem>
                                        {serviceCategories.map((cat) => (
                                            <MenuItem
                                                key={cat.serviceCategoryId ?? cat.ServiceCategoryId}
                                                value={String(cat.serviceCategoryId ?? cat.ServiceCategoryId)}
                                            >
                                                {cat.serviceCategoryName ?? cat.ServiceCategoryName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        />
                                    }
                                    label="Active"
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        
                        <Typography variant="h6" gutterBottom>
                            Checklist Items
                        </Typography>

                        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Item Name"
                                        value={itemName}
                                        onChange={(e) => setItemName(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddItem();
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Order"
                                        value={itemOrder}
                                        onChange={(e) => setItemOrder(parseInt(e.target.value) || 1)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={itemRequired}
                                                onChange={(e) => setItemRequired(e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label="Required"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={handleAddItem}
                                        size="small"
                                    >
                                        Add Item
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>

                        {checklistItems.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                                {checklistItems.map((item, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            p: 1,
                                            mb: 1,
                                            bgcolor: 'background.paper',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 1 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2">
                                                {item.itemText}
                                                {item.isRequired && (
                                                    <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />
                                                )}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Order: {item.sequenceOrder}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleMoveItem(index, 'up')}
                                                disabled={index === 0}
                                            >
                                                ↑
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleMoveItem(index, 'down')}
                                                disabled={index === checklistItems.length - 1}
                                            >
                                                ↓
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleRemoveItem(index)}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleSaveClick} variant="contained" color="primary">
                        {editingChecklist ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Items Dialog */}
            <Dialog open={openItemsDialog} onClose={() => setOpenItemsDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Checklist Items - {viewingChecklist?.name}
                </DialogTitle>
                <DialogContent>
                    {viewingChecklist?.checklistItems?.length > 0 ? (
                        <Box sx={{ mt: 2 }}>
                            {viewingChecklist.checklistItems
                                .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
                                .map((item, index) => (
                                    <Box
                                        key={item.id || index}
                                        sx={{
                                            p: 2,
                                            mb: 1,
                                            bgcolor: 'grey.50',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <Typography variant="body1">
                                            {index + 1}. {item.itemText}
                                        </Typography>
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                            <Chip
                                                label={item.isRequired ? 'Required' : 'Optional'}
                                                size="small"
                                                color={item.isRequired ? 'error' : 'default'}
                                            />
                                            <Chip
                                                label={`Order: ${item.sequenceOrder}`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </Box>
                                ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            No items in this checklist.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenItemsDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </main>
    );
}

